import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import CommonTable from '../../components/commonComponent/CommanTable';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const UserCategoryPage = () => {
  const [userCategories, setUserCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [formValues, setFormValues] = useState({
    name: '',
  });

  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUserCategories();
  }, []);

  /* ================= FETCH ================= */

  const fetchUserCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/api/user-category');
      setUserCategories(res.data.userCategories || []);
    } catch (err) {
      toastError('Failed to fetch user categories');
    } finally {
      setLoading(false);
    }
  };

  /* ================= SAVE ================= */

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formValues.name,
      };

      if (editingId) {
        await axios.put(`/admin/api/user-category/${editingId}`, payload);
        toastSuccess('User Category updated successfully!');
      } else {
        await axios.post('/admin/api/user-category', payload);
        toastSuccess('User Category added successfully!');
      }

      fetchUserCategories();
      handleCancel();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save user category');
    } finally {
      setSaving(false);
    }
  };

  /* ================= ACTIONS ================= */

  const handleEdit = (item) => {
    setFormValues({ name: item.name || '' });
    setEditingId(item._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const res = await confirmDelete('Are you sure you want to delete this user category?');
    if (!res.isConfirmed) return;

    try {
      await axios.delete(`/admin/api/user-category/${id}`);
      toastSuccess('User Category deleted successfully!');
      fetchUserCategories();
    } catch (err) {
      toastError('Failed to delete user category');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '' });
    setEditingId(null);
    setErrors({});
  };

  /* ================= TABLE ================= */

  const tableColumns = [
    { key: 'srNo', label: 'Sr No.' },
    { key: 'name', label: 'User Category Name' },
    { key: 'actions', label: 'Actions' },
  ];

  const filteredData = userCategories.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tableData = filteredData.map((item, index) => ({
    srNo: index + 1,
    name: item.name,
    actions: (
      <div>
        <button
          className="btn btn-sm me-1 border-0 text-primary"
          onClick={() => handleEdit(item)}
        >
          <FaEdit size={14} />
        </button>
      </div>
    ),
  }));

  /* ================= UI ================= */

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 mt-4">User Categories</h3>

        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search User Category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 250 }}
          />
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add User Category
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <CommonTable columns={tableColumns} data={tableData} />
      )}

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit User Category' : 'Add User Category'}
        fields={[
          {
            name: 'name',
            label: 'User Category Name',
            type: 'text',
            placeholder: 'Enter User Category',
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
        requiredFields={['name']}
        disabled={saving}
      />
    </div>
  );
};

export default UserCategoryPage;
